import type { Branch, DailySale } from './domain';

export type ReportMode = 'create' | 'update';

export class ReportSubmissionError extends Error {
  constructor(public readonly code: 'incomplete' | 'conflict' | 'request_failed', message: string) {
    super(message);
    this.name = 'ReportSubmissionError';
  }
}

export function prepareReportSales(branches: Branch[], sales: DailySale[], date: string) {
  const byBranch = new Map(sales.filter((sale) => sale.salesDate === date).map((sale) => [sale.branchId, sale]));
  const missingBranchNames: string[] = [];
  const prepared: DailySale[] = [];

  for (const branch of branches.filter((item) => item.isActive).sort((a, b) => a.sortOrder - b.sortOrder)) {
    const sale = byBranch.get(branch.id);
    if (!sale || sale.salesAmount === null || !Number.isFinite(sale.salesAmount) || sale.salesAmount < 0) {
      missingBranchNames.push(branch.name);
      continue;
    }
    prepared.push(sale);
  }

  return { sales: prepared, missingBranchNames };
}

export async function sendReport(input: {
  mode: ReportMode;
  salesDate: string;
  sales: DailySale[];
  deviceId: string;
  sessionId: string;
}) {
  const response = await fetch('/api/report', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...input, submissionId: crypto.randomUUID(), submittedAt: new Date().toISOString() })
  });
  if (response.status === 409) throw new ReportSubmissionError('conflict', 'รายงานวันนี้ถูกส่งไว้แล้ว');
  if (!response.ok) {
    const result = await response.json().catch(() => ({})) as { error?: string };
    throw new ReportSubmissionError('request_failed', result.error || 'ส่งรายงานไม่สำเร็จ');
  }
  return response.json() as Promise<{ ok: true; mode: ReportMode; branchCount: number }>;
}

export async function submitThenCopy<T>(submit: () => Promise<T>, copy: () => Promise<void>) {
  const result = await submit();
  try {
    await copy();
    return { result, copied: true as const };
  } catch {
    return { result, copied: false as const };
  }
}
